import React, { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import { AuthProvider } from "@/hooks/useAuth";

// Create singleton queryClient to prevent recreation on HMR
let queryClient: QueryClient;

export const getQueryClient = () => {
  if (!queryClient) {
    queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: 1,
          staleTime: 5 * 60 * 1000,
          gcTime: 10 * 60 * 1000,
        },
      },
    });
  }
  return queryClient;
};

export const AppProviders = ({ children }: { children: ReactNode }) => {
  // CRITICAL GUARD: Verificar se React hooks estão disponíveis
  // Previne erro: "Cannot read properties of null (reading 'useEffect')"
  if (typeof React.useEffect !== 'function') {
    console.error('CRITICAL: React hooks not available in AppProviders');
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-white text-center max-w-md">
          <h1 className="text-2xl font-bold mb-4">Erro crítico de inicialização</h1>
          <p className="text-white/60 mb-6">
            O React não foi inicializado corretamente. Por favor, recarregue a página.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-luxury-pinkBright hover:bg-luxury-pink text-white rounded-lg transition-colors"
          >
            Recarregar Página
          </button>
        </div>
      </div>
    );
  }

  // Direct call to singleton QueryClient (safe, no hooks needed)
  const client = getQueryClient();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <AuthProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <TooltipProvider>
              <Toaster
                position="top-center"
                expand={true}
                richColors={false}
                closeButton={true}
                offset={20}
                toastOptions={{
                  style: {
                    background: "rgba(0, 0, 0, 0.95)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    color: "white",
                    backdropFilter: "blur(16px)",
                    boxShadow:
                      "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)",
                    borderRadius: "4px",
                    padding: "16px",
                    minWidth: "320px",
                    maxWidth: "420px",
                  },
                  className: "backdrop-blur-xl shadow-2xl",
                  duration: 4000,
                }}
              />
              {children}
            </TooltipProvider>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default AppProviders;
